import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useFact } from "@/lib/factoryI18n";
import { Plus, Trash2 } from "lucide-react";

const BLANK_PIECE = () => ({ intake_category: "Unfinished", design_code: "", colour: "", size_text: "", sq_ft: 0, po_line_id: "", damage_note: "" });

export default function GRNEntry() {
  const { lang } = useLang();
  const f = useFact(lang);
  const [srcType, setSrcType] = useState("Weaver delivery");
  const [weavers, setWeavers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [pos, setPos] = useState([]);
  const [poId, setPoId] = useState("");
  const [poLines, setPoLines] = useState([]);
  const [weaverId, setWeaverId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [challan, setChallan] = useState("");
  const [expected, setExpected] = useState(1);
  const [pieces, setPieces] = useState([BLANK_PIECE()]);
  const [msg, setMsg] = useState("");
  const [createdGrn, setCreatedGrn] = useState(null);

  useEffect(() => {
    api.get("/weavers").then((r) => setWeavers(r.data));
    api.get("/vendors").then((r) => setVendors(r.data));
    api.get("/pos").then((r) => setPos(r.data));
  }, []);

  useEffect(() => {
    if (poId) api.get(`/pos/${poId}`).then((r) => setPoLines(r.data.line_items || []));
    else setPoLines([]);
  }, [poId]);

  const addPiece = () => setPieces((prev) => [...prev, BLANK_PIECE()]);
  const removePiece = (i) => setPieces((prev) => prev.filter((_, idx) => idx !== i));
  const updatePiece = (i, patch) => setPieces((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  const submit = async () => {
    setMsg("");
    try {
      const body = {
        source_type: srcType,
        weaver_id: weaverId || null,
        vendor_id: vendorId || null,
        po_id: poId || null,
        vehicle, challan_ref: challan, expected_count: Number(expected),
        pieces,
      };
      const r = await api.post("/factory/grns", body);
      setCreatedGrn(r.data);
      setMsg(`Created ${r.data.grn_number}: ${r.data.pieces?.length || 0} piece(s).`);
      setPieces([BLANK_PIECE()]);
    } catch (e) {
      setMsg("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="max-w-4xl" data-testid="grn-entry">
      <h1 className="text-2xl font-bold mb-4" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>{f("grn")}</h1>

      <div className="bg-white border border-[#1B3A5C]/10 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <L label={f("source_type")}>
            <select data-testid="grn-source-type" value={srcType} onChange={(e) => setSrcType(e.target.value)} className="w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm">
              <option>Weaver delivery</option><option>Trading vendor — FG</option><option>Trading vendor — SF</option><option>In-house manufacturing</option>
            </select>
          </L>
          {srcType === "Weaver delivery" ? (
            <>
              <L label="Weaver"><select data-testid="grn-weaver" value={weaverId} onChange={(e) => setWeaverId(e.target.value)} className="w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm"><option value="">—</option>{weavers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}</select></L>
              <L label="PO">
                <select data-testid="grn-po" value={poId} onChange={(e) => setPoId(e.target.value)} className="w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm">
                  <option value="">—</option>
                  {pos.filter((p) => !weaverId || p.weaver_id === weaverId).slice(0, 200).map((p) => <option key={p.id} value={p.id}>{p.po_number}</option>)}
                </select>
              </L>
            </>
          ) : (
            <L label="Vendor"><select data-testid="grn-vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value)} className="w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm"><option value="">—</option>{vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select></L>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <L label={f("vehicle")}><input data-testid="grn-vehicle" value={vehicle} onChange={(e) => setVehicle(e.target.value)} className="w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" /></L>
          <L label={f("challan")}><input data-testid="grn-challan" value={challan} onChange={(e) => setChallan(e.target.value)} className="w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" /></L>
          <L label={f("expected_count")}><input data-testid="grn-expected" type="number" value={expected} onChange={(e) => setExpected(e.target.value)} className="w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" /></L>
        </div>
      </div>

      <h2 className="mt-6 font-bold" style={{ color: "var(--brand-navy)" }}>{f("pieces")}</h2>
      <div className="mt-2 space-y-2">
        {pieces.map((p, i) => (
          <div key={i} className="hag-surface p-3 grid grid-cols-2 md:grid-cols-6 gap-2 items-end" data-testid={`grn-piece-row-${i}`}>
            <L label={f("intake_category")}>
              <select data-testid={`grn-piece-intake-${i}`} value={p.intake_category} onChange={(e) => updatePiece(i, { intake_category: e.target.value })} className="w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm">
                <option>Finished</option><option>QC Issue</option><option>Unfinished</option>
              </select>
            </L>
            {poLines.length > 0 && (
              <L label="PO line">
                <select data-testid={`grn-piece-line-${i}`} value={p.po_line_id || ""} onChange={(e) => { const ln = poLines.find((x) => x.id === e.target.value); updatePiece(i, { po_line_id: e.target.value, design_code: ln?.design_code || p.design_code, size_text: ln?.size_text || p.size_text, colour: ln?.colour || p.colour, sq_ft: ln?.sq_ft || p.sq_ft }); }} className="w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm">
                  <option value="">—</option>
                  {poLines.map((ln) => <option key={ln.id} value={ln.id}>{ln.carpet_number || ln.design_code}</option>)}
                </select>
              </L>
            )}
            <L label={f("design")}><input data-testid={`grn-piece-design-${i}`} value={p.design_code} onChange={(e) => updatePiece(i, { design_code: e.target.value })} className="w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" /></L>
            <L label={f("size")}><input data-testid={`grn-piece-size-${i}`} value={p.size_text} onChange={(e) => updatePiece(i, { size_text: e.target.value })} className="w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" /></L>
            <L label={f("colour")}><input data-testid={`grn-piece-colour-${i}`} value={p.colour} onChange={(e) => updatePiece(i, { colour: e.target.value })} className="w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" /></L>
            {p.intake_category === "QC Issue" && (
              <L label="Issue note"><input data-testid={`grn-piece-note-${i}`} value={p.damage_note} onChange={(e) => updatePiece(i, { damage_note: e.target.value })} className="w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" /></L>
            )}
            <button onClick={() => removePiece(i)} className="h-10 flex items-center justify-center border border-[#1B3A5C]/15 rounded-md text-red-600" data-testid={`grn-piece-remove-${i}`}><Trash2 size={14} /></button>
          </div>
        ))}
        <button data-testid="grn-add-piece" onClick={addPiece} className="h-10 px-3 rounded-md text-sm border border-dashed border-[#1B3A5C]/25 w-full flex items-center justify-center gap-2"><Plus size={14} /> {f("add_piece")}</button>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button data-testid="grn-submit" onClick={submit} className="px-4 py-2.5 rounded-full font-semibold text-sm" style={{ background: "var(--brand-navy)", color: "white" }}>
          Save GRN
        </button>
        {createdGrn && (
          <a data-testid="grn-print-qr" href={`/factory/qr-labels/${createdGrn.id}`} target="_blank" rel="noreferrer" className="px-4 py-2.5 rounded-full font-semibold text-sm border border-[#C8A86B]" style={{ color: "var(--brand-navy)" }}>
            {f("print_qr_labels")}
          </a>
        )}
        {msg && <span data-testid="grn-msg" className="text-sm">{msg}</span>}
      </div>
    </div>
  );
}

const L = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-widest text-[#7C8E9E] mb-1">{label}</div>
    {children}
  </label>
);
